import type {
  ColumnConfig,
  ColumnsResponse,
  PreviewResponse,
} from '@app/application/report/column-editor/types'

import reportDisplayParams from '@app/application/report/utils/report-display-params'
import SFetch from '@app/utils/sfetch'
import { decamelizeBody } from '@app/utils/sfetch/json'

// The report is read in a language and with an account display of the reader's
// choosing, and both live in the page's query string rather than in the session
// — so a render asked for over POST has to carry them or the server falls back
// to the reader's own locale and the report's stored display options, and the
// applied table comes back in a different language than the one it replaced.
const reportRenderContext = (): Record<string, string> => {
  const language = new URLSearchParams(window.location.search).get('language')

  return {
    ...reportDisplayParams(),
    ...(language ? { language } : {}),
  }
}

export async function fetchColumnsRequest(params: {
  reportId: number
  reportPeriodSetId: number | null
}): Promise<ColumnsResponse> {
  // A report can be viewed with a specific period set selected (see the
  // switcher in reports/_header_row.html.slim), so the drawer has to ask for
  // that set's columns instead of letting the server fall back to the active
  // one — otherwise it lists columns the table on screen isn't showing.
  const url = window.Silverfin.Routes.report_columns_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    params.reportId,
    params.reportPeriodSetId
      ? { report_period_set_id: params.reportPeriodSetId }
      : {}
  )

  // Camelization is off so the schema's snake_case field keys and each column's
  // value keys line up with what the server sends.
  const response = await SFetch.json<ColumnsResponse>(
    url,
    {},
    { camelizeJSON: false }
  )
  if (!response) throw new Error('Empty report columns response')

  return response
}

// Asks what columns the report would render for the configs the drawer holds
// right now. Headers only — the periods a config expands into depend on the
// company's book years and which ledgers exist, so the server works them out.
export async function fetchColumnsPreviewRequest(params: {
  reportId: number
  columns: ColumnConfig[]
}): Promise<PreviewResponse> {
  const url = window.Silverfin.Routes.report_columns_preview_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    params.reportId
  )

  // As with the columns request: no camelization on the way back, so the labels
  // and value keys come in as the presenter named them. The body is decamelized
  // on the way out — see SFetch.json — which the snake_case field keys the form
  // schema ships come through unchanged. The apply request below serialises the
  // same payload the same way, deliberately.
  const response = await SFetch.json<PreviewResponse>(
    url,
    { method: 'POST', body: { columns: params.columns } },
    { camelizeJSON: false }
  )
  if (!response) throw new Error('Empty report columns preview response')

  return response
}

// The report table rendered for the columns the drawer holds — HTML rather than
// JSON, because it is the report's own `reports/_table_content` partial. Nothing
// is saved: the server renders from these columns for this request alone.
export async function fetchAppliedColumnsRequest(params: {
  reportId: number
  columns: ColumnConfig[]
}): Promise<string> {
  const url = window.Silverfin.Routes.apply_columns_report_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    params.reportId
  )

  // Serialised by the same function SFetch.json uses for the preview body, so
  // the two cannot drift. Both endpoints read the result with the same
  // `Reports::ColumnsFromConfig`, and a key that survives one request but not
  // the other would have the user previewing one report and applying another —
  // the single thing the preview exists to rule out.
  const html = await SFetch.html(url, {
    method: 'POST',
    body: decamelizeBody({
      columns: params.columns,
      ...reportRenderContext(),
    }),
    headers: { 'Content-Type': 'application/json' },
  })
  if (html === null) throw new Error('Empty applied report columns response')

  return html
}
